import PropTypes from 'prop-types';
import React from 'react';
import Lightbox from 'react-image-lightbox';
import { Loader2 } from 'lucide-react';
import StaffPicks from 'components/Badges/StaffPicks/StaffPicks';
import SvgIcon from 'components/SvgIcon/SvgIcon';
import { getThumbnailByWidth } from 'helpers/HelperFunctions';
import { preferSquareProjectCover } from 'helpers/projectCoverAspect';
import styles from './ImagePlayer.module.scss';
import 'react-image-lightbox/style.css';

/**
 * Prefer real/optimised originals from the API; fall back to a client preview
 * while derivatives are still processing. Never return camera placeholder assets
 * for projects whose upload succeeded.
 */
function getDisplayImage(
  thumbnails,
  { clientPreviewUrl, skipPlaceholder, preferSquare }
) {
  if (thumbnails && !thumbnails.placeholder) {
    // Album-style audio covers: prefer square derivatives over the uncropped
    // original so the project hero matches the crop the user saved.
    if (
      preferSquare &&
      thumbnails.images &&
      Object.keys(thumbnails.images).length > 0
    ) {
      try {
        const sized = getThumbnailByWidth(thumbnails.images, 500, {
          square: true,
        });
        if (sized) {
          return sized;
        }
      } catch (e) {
        // fall through
      }
    }

    if (thumbnails.original_optimised) {
      return thumbnails.original_optimised;
    }

    if (thumbnails.original) {
      return thumbnails.original;
    }

    if (thumbnails.images && Object.keys(thumbnails.images).length > 0) {
      try {
        const sized = getThumbnailByWidth(thumbnails.images, 500, {
          square: preferSquare,
        });
        if (sized) {
          return sized;
        }
      } catch (e) {
        // fall through
      }
    }
  }

  if (clientPreviewUrl) {
    return clientPreviewUrl;
  }

  if (!thumbnails) {
    return '';
  }

  if (thumbnails.placeholder && skipPlaceholder) {
    return '';
  }

  if (
    preferSquare &&
    thumbnails.images &&
    Object.keys(thumbnails.images).length > 0
  ) {
    try {
      const sized = getThumbnailByWidth(thumbnails.images, 500, {
        square: true,
      });
      if (sized) {
        return sized;
      }
    } catch (e) {
      // fall through
    }
  }

  if (thumbnails.original_optimised) {
    return thumbnails.original_optimised;
  }

  if (thumbnails.original) {
    return thumbnails.original;
  }

  if (thumbnails.images && Object.keys(thumbnails.images).length > 0) {
    try {
      return (
        getThumbnailByWidth(thumbnails.images, 500, {
          square: preferSquare,
        }) || ''
      );
    } catch (e) {
      return '';
    }
  }

  return '';
}

class ImagePlayer extends React.Component {
  static propTypes = {
    clientPreviewUrl: PropTypes.string,
    onLoad: PropTypes.func,
    onZoom: PropTypes.func,
    project: PropTypes.object.isRequired,
    showDisabledPlay: PropTypes.bool,
    staffPicks: PropTypes.bool,
    user: PropTypes.object,
    zoom: PropTypes.bool,
  };

  static defaultProps = {
    clientPreviewUrl: null,
    showDisabledPlay: false,
    zoom: true,
  };

  constructor() {
    super();

    this.state = {
      imgLoaded: false,
      zoomed: false,
    };

    this.handleLoadImage = this.handleLoadImage.bind(this);
  }

  componentDidMount() {
    if (this.refs.hiddenImage && this.refs.hiddenImage.complete) {
      this.handleLoadImage();
    }
  }

  componentDidUpdate(prevProps) {
    const prevSrc =
      prevProps.clientPreviewUrl ||
      (prevProps.project &&
        prevProps.project.thumbnails &&
        (prevProps.project.thumbnails.original_optimised ||
          prevProps.project.thumbnails.original));
    const nextSrc =
      this.props.clientPreviewUrl ||
      (this.props.project &&
        this.props.project.thumbnails &&
        (this.props.project.thumbnails.original_optimised ||
          this.props.project.thumbnails.original));
    if (prevSrc !== nextSrc) {
      this.setState({ imgLoaded: false });
    }
  }

  handleLoadImage() {
    this.setState({
      imgLoaded: true,
    });

    if (this.props.onLoad) {
      this.props.onLoad();
    }
  }

  handleZoom() {
    if (this.props.onZoom) {
      this.props.onZoom();
    }
  }

  render() {
    const {
      clientPreviewUrl,
      project,
      showDisabledPlay,
      staffPicks,
      zoom,
      user,
    } = this.props;
    const { imgLoaded, zoomed } = this.state;
    const { thumbnails } = project;
    const isProcessing = project.state === 'processing';
    const isPlaceholder = Boolean(thumbnails && thumbnails.placeholder);
    const preferSquare = preferSquareProjectCover(project);
    const skipPlaceholder =
      isProcessing ||
      Boolean(clientPreviewUrl) ||
      ['image', 'video', 'audio'].includes(project.mediaType);
    const displayImage = getDisplayImage(thumbnails, {
      clientPreviewUrl,
      skipPlaceholder,
      preferSquare,
    });
    // Hero may use a square derivative; Lightbox should open the full original.
    const lightboxSrc =
      (!isPlaceholder &&
        thumbnails &&
        (thumbnails.original_optimised || thumbnails.original)) ||
      displayImage;
    const hasRealOriginal = Boolean(
      (thumbnails && !isPlaceholder && thumbnails.original_optimised) ||
        (thumbnails && !isPlaceholder && thumbnails.original) ||
        clientPreviewUrl
    );
    const canZoom = zoom && hasRealOriginal && !isProcessing;
    const processingLabel =
      project.mediaType === 'video'
        ? 'Processing HD version'
        : project.mediaType === 'audio'
        ? 'Processing audio'
        : 'Processing image';

    return (
      <div>
        <span
          itemProp="embedUrl"
          content={
            project && project.links && project.links.widget
              ? project.links.widget
              : ''
          }
        />
        <span itemProp="thumbnail" content={displayImage} />
        <span
          itemProp="datePublished"
          content={project && project.created_at ? project.created_at : ''}
        />
        <span
          itemProp="author"
          content={user && user.displayname ? user.displayname : ''}
        />
        <div
          className={`${styles.imageContainer} ${
            preferSquare ? styles.square : ''
          } ${canZoom ? styles.zoom : ''} ${
            !displayImage ? styles.emptyMedia : ''
          }`}
          onClick={() => (canZoom ? this.setState({ zoomed: !zoomed }) : null)}
        >
          {displayImage ? (
            <>
              <div
                className={`${styles.blurFill} ${
                  imgLoaded ? styles.blurFillLoaded : ''
                }`}
                style={{ backgroundImage: `url('${displayImage}')` }}
                aria-hidden
              />
              <div
                className={`${styles.backgroundDiv} ${
                  imgLoaded ? styles.backgroundLoaded : ''
                }`}
                style={{ backgroundImage: `url('${displayImage}')` }}
              />
              <img
                alt="Twine"
                className={styles.hiddenImage}
                ref="hiddenImage"
                onLoad={() => this.handleLoadImage()}
                src={displayImage}
              />
            </>
          ) : null}
          {showDisabledPlay && (
            <div className={styles.disabledPlay} aria-hidden>
              <span className={styles.disabledPlayIcon} />
            </div>
          )}
          {isProcessing && (
            <div className={styles.processingPill} role="status">
              <Loader2
                className={styles.processingSpinner}
                size={14}
                strokeWidth={2.5}
                aria-hidden
              />
              <span>{processingLabel}</span>
            </div>
          )}
        </div>
        {staffPicks ? (
          <StaffPicks
            project={project}
            style={{
              position: 'absolute',
              width: '20%',
              bottom: '15px',
              left: '15px',
            }}
          />
        ) : null}
        {canZoom && (
          <SvgIcon
            icon="zoom"
            color="white"
            size="20"
            className={styles.zoomIcon}
          />
        )}
        {canZoom && zoomed ? (
          <Lightbox
            mainSrc={lightboxSrc}
            discourageDownloads
            onCloseRequest={() => this.setState({ zoomed: false })}
          />
        ) : null}
      </div>
    );
  }
}

export default ImagePlayer;
